import { useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tabs,
  Tab,
  Box,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { postRegion } from '../../api/regionAPI';

const AddRegionDialog = ({ open, onClose, regions, onRegionAdded }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [showInfo, setShowInfo] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentRegion: ''
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const getRegionLevel = (region, allRegions) => {
    if (!region) return 0;
    if (region.parentRegion === null) return 1;

    const parentRegion = allRegions.find(r => r.idRegion === region.parentRegion);
    if (!parentRegion) return 1;

    return getRegionLevel(parentRegion, allRegions) + 1;
  };

  // Alt Bölge Ekle için sadece bölgeleri (level 2) getir
  const getLevel2Regions = () => {
    if (!regions || regions.length === 0) return [];

    return regions.filter(region => {
      const level = getRegionLevel(region, regions);
      return level === 2;
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Tab değiştiğinde formu sıfırla
    setFormData({
      name: '',
      description: '',
      parentRegion: ''
    });
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.name) {
        setError(t('regions.dialogs.addRegion.nameRequired', 'Bölge adı zorunludur'));
        return;
      }

      // Alt Bölge Ekle tab'ında bağlı bölge zorunlu
      if (activeTab === 1 && !formData.parentRegion) {
        setError(t('regions.dialogs.addRegion.parentRequired', 'Bağlı olduğu bölge zorunludur'));
        return;
      }

      const adminId = localStorage.getItem('adminId');
      if (!adminId) {
        setError(t('regions.dialogs.addRegion.adminNotFound', 'Admin ID bulunamadı. Lütfen tekrar giriş yapın.'));
        return;
      }

      let parentRegionId = null;

      if (activeTab === 0) {
        // Bölge Ekle - Root bölgeye ekle
        if (regions && regions.length > 0) {
          const rootRegion = regions.find(r => r.parentRegion === null);
          if (rootRegion) {
            parentRegionId = rootRegion.idRegion;
          }
        }
      } else {
        // Alt Bölge Ekle - Seçilen bölgeye ekle
        parentRegionId = formData.parentRegion;
      }

      const requestData = {
        code: formData.name,
        name: formData.name,
        description: formData.description,
        admin_idAdmin: parseInt(adminId),
        parentRegion: parentRegionId
      };

      await postRegion(requestData);

      setSnackbar({
        open: true,
        message: t('regions.messages.regionCreated'),
        severity: 'success'
      });

      if (onRegionAdded) {
        onRegionAdded();
      }

      setFormData({
        name: '',
        description: '',
        parentRegion: ''
      });
      onClose();
    } catch (err) {
      setError(err.message || t('regions.messages.errorCreatingRegion', 'Bölge eklenirken bir hata oluştu'));
      console.error('Error adding region:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        description: '',
        parentRegion: ''
      });
      setError(null);
      setActiveTab(0);
      setShowInfo(true);
      onClose();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const level2Regions = getLevel2Regions();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', pt: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} centered>
          <Tab label={t('regions.dialogs.addRegion.addRegionTab', 'Bölge Ekle')} />
          <Tab label={t('regions.dialogs.addRegion.addSubRegionTab', 'Alt Bölge Ekle')} />
        </Tabs>
      </Box>

      <DialogContent dividers sx={{ pb: 3 }}>
        {/* Kapatılabilir Açıklama */}
        <Collapse in={showInfo}>
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setShowInfo(false)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              Bölge ve Alt Bölge Nasıl Çalışır?
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Örnek: <strong>İstanbul → Kadıköy</strong> veya <strong>Kocaeli → Kocaeli Üniversitesi</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>1.</strong> Bölge oluşturmak için önce <strong>"Bölge Ekle"</strong> sekmesinde bölge adını (örn: Kocaeli) ve açıklamasını girin, ardından ekleyin.
            </Typography>
            <Typography variant="body2">
              <strong>2.</strong> Alt bölge oluşturmak için <strong>"Alt Bölge Ekle"</strong> sekmesinde alt bölge adını (örn: Kocaeli Üniversitesi) yazın, açıklamasını girin ve bağlı olduğu bölgeyi (Kocaeli) seçin.
            </Typography>
          </Alert>
        </Collapse>

        {!showInfo && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              startIcon={<InfoOutlinedIcon />}
              onClick={() => setShowInfo(true)}
              sx={{ textTransform: 'none' }}
            >
              Nasıl çalışır?
            </Button>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          margin="dense"
          name="name"
          label={t('regions.dialogs.addRegion.name', 'Bölge Adı')}
          type="text"
          fullWidth
          variant="outlined"
          value={formData.name}
          onChange={handleInputChange}
          disabled={loading}
          required
          sx={{ mb: 2, mt: 1 }}
        />

        <TextField
          margin="dense"
          name="description"
          label={t('regions.dialogs.addRegion.descriptionField', 'Açıklama')}
          type="text"
          fullWidth
          variant="outlined"
          multiline
          rows={3}
          value={formData.description}
          onChange={handleInputChange}
          disabled={loading}
          sx={{ mb: 2 }}
          InputProps={{
            sx: { alignItems: 'flex-start' }
          }}
        />

        {/* Alt Bölge Ekle tab'ında bağlı bölge seçimi göster */}
        {activeTab === 1 && (
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }} required>
            <InputLabel>{t('regions.dialogs.addRegion.parentRegion', 'Bağlı Olduğu Bölge')}</InputLabel>
            <Select
              name="parentRegion"
              value={formData.parentRegion}
              onChange={handleInputChange}
              label={t('regions.dialogs.addRegion.parentRegion', 'Bağlı Olduğu Bölge')}
              disabled={loading}
            >
              {level2Regions.map((region) => (
                <MenuItem key={region.idRegion} value={region.idRegion}>
                  {region.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? t('common.saving') : t('common.add')}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AddRegionDialog;
